import { css } from 'lit';
import { hostReset, floatingPanel, placementOrigins } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  floatingPanel('.panel', { durationVar: '--uwc-menu-duration', durationDefault: '140ms' }),
  placementOrigins,
  css`
    :host { display: contents; }
    slot[name="trigger"] { display: contents; }

    /* ── Inline mode ────────────────────────────────────────────────────────── */
    :host([inline]) {
      display: inline-block;
    }
    /* Revert all floating-panel positioning so the panel sits in normal document
       flow and never enters the browser top layer. */
    :host([inline]) #panel {
      position: relative !important;
      inset: unset !important;
      margin: 0;
      opacity: 1 !important;
      transform: none !important;
      transition: none !important;
      display: block !important;
      z-index: auto !important;
      /* Remove popup drop-shadow; use a visible border instead */
      box-shadow: none !important;
      border: 1px solid ${t.borderControl} !important;
    }

    /* ── Floating panel (visual) ────────────────────────────────────────────── */
    .panel {
      background:    var(--uwc-menu-bg,     ${t.surface});
      border:        var(--uwc-menu-border, 1px solid ${t.border});
      border-radius: var(--uwc-menu-radius, ${t.radiusMd});
      box-shadow:    var(--uwc-menu-shadow, ${t.shadowLg});
      min-width:     var(--uwc-menu-min-width, 180px);
      z-index:       var(--uwc-menu-z, ${t.zFloat});
      /* No overflow:hidden — submenus extend outside the panel boundary */
    }

    /* ── Start / end slots ──────────────────────────────────────────────────── */
    slot[name="start"],
    slot[name="end"] { display: block; }

    /* ── List ───────────────────────────────────────────────────────────────── */
    .list { list-style: none; padding: 0; margin: 0; }

    /* ── Separator ──────────────────────────────────────────────────────────── */
    .separator { height: 1px; background: var(--uwc-menu-separator-color, var(--uwc-border-subtle, ${t.borderSubtle})); margin: ${t.space1} 0; }

    /* ── Group header ───────────────────────────────────────────────────────── */
    .group-header {
      padding: ${t.space2} 13px 4px;
      font-size: 10.5px;
      font-weight: ${t.fontWeightSemibold};
      letter-spacing: 0.07em;
      text-transform: uppercase;
      color: var(--uwc-menu-group-header-color, var(--uwc-text-muted, ${t.textMuted}));
      user-select: none;
    }
    .group-header:not(:first-child) {
      margin-top: ${t.space1};
      border-top: 1px solid var(--uwc-menu-separator-color, var(--uwc-border-subtle, ${t.borderSubtle}));
      padding-top: ${t.space2};
    }

    /* ── Item ───────────────────────────────────────────────────────────────── */
    .item { position: relative; }

    .item-inner {
      display: flex;
      align-items: center;
      gap: 9px;
      width: 100%;
      padding: ${t.space2} 13px;
      /* md is the intended default (the themes used to pin it via
         --uwc-menu-item-font-size); consuming the global token directly here
         lets menu items rescale with the size/font presets. */
      font-size:   var(--uwc-menu-item-font-size, ${t.fontSizeMd});
      font-family: var(--uwc-font-family, inherit);
      color:       var(--uwc-menu-item-color, var(--uwc-text, ${t.text}));
      background:  transparent;
      border:      none;
      cursor:      pointer;
      text-align:  left;
      text-decoration: none;
      border-radius: 0;
      outline: none;
      white-space: nowrap;
      transition: background ${t.durationFast}, color ${t.durationFast};
    }
    .item-inner:hover,
    .item-inner:focus-visible {
      background: var(--uwc-menu-item-hover-bg,    ${t.hoverBg});
      color:      var(--uwc-menu-item-hover-color, var(--uwc-text, ${t.text}));
    }
    .item--disabled .item-inner {
      color:  var(--uwc-menu-item-disabled-color, var(--uwc-text-disabled, ${t.textDisabled}));
      cursor: not-allowed;
    }
    .item--disabled .item-inner:hover { background: transparent; }
    .item--active > .item-inner { background: var(--uwc-menu-item-hover-bg, ${t.hoverBg}); }
    .item--danger > .item-inner { color: var(--uwc-menu-item-danger-color, ${t.danger}); }
    .item--danger > .item-inner:hover {
      background: color-mix(in oklab, var(--uwc-menu-item-danger-color, ${t.danger}) 10%, transparent);
      color: var(--uwc-menu-item-danger-color, ${t.danger});
    }
    .item--selected > .item-inner {
      background: var(--uwc-menu-item-selected-bg, color-mix(in oklab, ${t.primary} 10%, transparent));
      color:      var(--uwc-menu-item-selected-color, ${t.primary});
      font-weight: ${t.fontWeightMedium};
    }
    .item--selected > .item-inner:hover {
      background: var(--uwc-menu-item-selected-bg, color-mix(in oklab, ${t.primary} 14%, transparent));
    }

    /* ── Item sub-elements ──────────────────────────────────────────────────── */
    .item-icon {
      width: 18px;
      text-align: center;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .item-label { flex: 1; line-height: 1; }
    .item-badge {
      font-size: ${t.fontSizeXs};
      padding: 1px 6px;
      border-radius: ${t.radiusFull};
      background: var(--uwc-border-subtle, ${t.borderSubtle});
      color: var(--uwc-text-secondary, ${t.textSecondary});
      font-weight: ${t.fontWeightMedium};
      flex-shrink: 0;
    }
    .item-chevron { margin-left: auto; color: var(--uwc-text-muted, ${t.textMuted}); display: flex; align-items: center; flex-shrink: 0; }

    /* ── Submenu panel ──────────────────────────────────────────────────────── */
    .submenu-panel {
      position: absolute;
      left: calc(100% + 4px);
      top: 0;
      list-style: none;
      padding: ${t.space1} 0;
      margin: 0;
      background:    var(--uwc-menu-bg,     ${t.surface});
      border:        var(--uwc-menu-border, 1px solid ${t.border});
      border-radius: var(--uwc-menu-radius, ${t.radiusMd});
      box-shadow:    var(--uwc-menu-shadow, ${t.shadowLg});
      min-width:     160px;
      z-index: 1;
      animation: submenu-in 120ms ${t.easingStandard} both;
    }

    @keyframes submenu-in {
      from { opacity: 0; transform: translateX(-4px) scale(0.97); }
      to   { opacity: 1; transform: translateX(0) scale(1); }
    }
  `,
];
